import { isScreenshotMode } from "@constants/common";
import { useExamContext } from "@features/exam/contexts/ExamContext";
import { hasUnlockedStudyPackContent } from "@features/exam/helpers";
import { hasProductAccess } from "@helpers/content/tag";
import { useCanBypassPaywall } from "@hooks/useCanBypassPaywall";
import { useProductMetadata } from "@hooks/useProduct";

type UseStudyPackAccessProductId = Parameters<typeof useProductMetadata>[1];

export const useStudyPackAccess = (
	studyPackProductId?: UseStudyPackAccessProductId,
) => {
	const {
		currentExamContext: { freeStudyPackCollectionId },
	} = useExamContext();
	const canBypassPaywall = useCanBypassPaywall();
	const {
		query: { data: studyPackMetadata, isLoading, isPending },
		productId: metadataProductId,
	} = useProductMetadata("STUDY_PACK", studyPackProductId);

	const hasStudyPackPurchaseAccess = hasProductAccess(studyPackMetadata);

	const hasPurchasedOrBypassedAccess =
		hasStudyPackPurchaseAccess || isScreenshotMode || canBypassPaywall;

	/** Product access, or free collection is being served — content should not be locked. */
	const hasStudyPackContentAccess = hasUnlockedStudyPackContent({
		hasStudyPackContentAccess: hasPurchasedOrBypassedAccess,
		freeStudyPackCollectionId,
	});

	return {
		hasStudyPackContentAccess,
		/** Effective purchase-or-bypassed access — picks free vs paid collection and gates upsell surfaces. */
		hasStudyPackPurchaseAccess: hasPurchasedOrBypassedAccess,
		freeStudyPackCollectionId,
		isLoading,
		isPending: isPending && Boolean(metadataProductId),
	};
};
